/**
 * Organization Type Configuration
 * Defines UI features and layout preferences for different organization types
 */

export interface AssetFieldType {
  type: string;           // Field type identifier (e.g., 'text', 'loan_officer_picker')
  label: string;          // Display label (e.g., 'Text', 'Loan Officer')
  component: string;      // UI component to use (e.g., 'TextInput', 'UserRolePicker')
  roleFilter?: string;    // Filter users by this owner role (for user picker types)
  validation?: {
    pattern?: string;     // Regex pattern for validation
    minLength?: number;
    maxLength?: number;
  };
}

export interface OrganizationTypeConfig {
  // Navigation menu items to show/hide
  menuItems: {
    home: boolean;
    tasks: boolean;
    'compliance-management': boolean;
    locations: boolean;
    'floor-map': boolean;
    agreements: boolean;  // Menu item ID is 'agreements' (label: 'Contracts')
    checklists: boolean;
    forms: boolean;
    assets: boolean;
    'evidence-vault': boolean;
  };
  
  // Home page features
  homePage: {
    showLocationScorecards: boolean;
    showCompanyMetrics: boolean;
    showComplianceReview?: boolean;  // Show Compliance Review section (for Bank orgs)
    /** Checklist run details: radio to show only NEEDS_REVIEW/FAIL items and actionable fields */
    showChecklistRunActionableFilter?: boolean;
    // defaultView is reserved for future use - will define custom home page layouts per org type
    defaultView?: 'scorecards' | 'assets' | 'tasks';
  };
  
  // Feature flags
  features: {
    multiLocation: boolean;
    contracts: boolean;
    locationFilter: boolean;
  };
  
  // Asset field types - available custom field types for asset types
  assetFieldTypes?: {
    default: AssetFieldType[];
    custom: AssetFieldType[];
  };
}

/**
 * Configuration map for different organization types
 */
export const ORG_TYPE_CONFIGS: Record<string, OrganizationTypeConfig> = {
  'Bank': {
    menuItems: {
      home: true,
      tasks: true,
      'compliance-management': true,
      locations: true,  // Locations enabled for banks
      'floor-map': false,  // Floor map not used for bank demo
      agreements: false,  // Contracts not needed for banks
      checklists: true,
      forms: true,
      assets: true,
      'evidence-vault': true,
    },
    homePage: {
      showLocationScorecards: false,  // Banks don't need location scorecards
      showCompanyMetrics: false,       // Banks don't need company-wide metrics
      showComplianceReview: true,      // Banks need Compliance Review section
      showChecklistRunActionableFilter: true,
      // defaultView will be used in next step to define custom home page layout
    },
    features: {
      multiLocation: false,     // Banks typically have single location
      contracts: false,          // Contracts feature not needed
      locationFilter: false,    // No location filtering needed
    },
    assetFieldTypes: {
      default: [
        { type: 'text', label: 'Text', component: 'TextInput' },
        { type: 'number', label: 'Number', component: 'NumberInput' },
        { type: 'date', label: 'Date', component: 'DateInput' },
        { type: 'email', label: 'Email', component: 'EmailInput' },
        { type: 'tel', label: 'Phone', component: 'TelInput' },
        { type: 'textarea', label: 'Text Area', component: 'TextArea' },
      ],
      custom: [
        {
          type: 'loan_officer_picker',
          label: 'Loan Officer',
          component: 'UserRolePicker',
          roleFilter: 'Loan Officer',
        },
        {
          type: 'alphanumeric',
          label: 'Loan Number',
          component: 'AlphanumericInput',
          validation: {
            pattern: '^[A-Za-z0-9]+$', // Only letters and numbers
          },
        },
      ],
    },
  },
  
  // Default configuration for all other organization types
  'default': {
    menuItems: {
      home: true,
      tasks: true,
      'compliance-management': true,
      locations: true,
      'floor-map': true,
      agreements: true,
      checklists: true,
      forms: true,
      assets: true,
      'evidence-vault': true,
    },
    homePage: {
      showLocationScorecards: true,
      showCompanyMetrics: true,
      defaultView: 'scorecards',
    },
    features: {
      multiLocation: true,
      contracts: true,
      locationFilter: true,
    },
    assetFieldTypes: {
      default: [
        { type: 'text', label: 'Text', component: 'TextInput' },
        { type: 'number', label: 'Number', component: 'NumberInput' },
        { type: 'date', label: 'Date', component: 'DateInput' },
        { type: 'email', label: 'Email', component: 'EmailInput' },
        { type: 'tel', label: 'Phone', component: 'TelInput' },
        { type: 'textarea', label: 'Text Area', component: 'TextArea' },
      ],
      custom: [], // No custom field types for default organization types
    },
  },
};

/**
 * Get configuration for a specific organization type
 * Falls back to 'default' if type not found
 */
export function getOrganizationTypeConfig(organizationType: string | null | undefined): OrganizationTypeConfig {
  if (!organizationType) {
    return ORG_TYPE_CONFIGS['default'];
  }
  
  return ORG_TYPE_CONFIGS[organizationType] || ORG_TYPE_CONFIGS['default'];
}

